import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { AlertTriangle, Copy, KeyRound, Loader2, Phone } from 'lucide-react'
import Modal from '../../../components/Modal/Modal'
import { useIssueMdmLocalCode } from '../../../hooks/useMdm'
import { errorMessage, formatUntil } from './mobileUtils'

const PURPOSES = [
  {
    value: 'admin',
    label: 'Modo técnico',
    hint: 'Abre o modo técnico no tablet: sair do quiosque por um tempo, pausar o MDM, ver o diagnóstico.',
  },
  {
    value: 'remove',
    label: 'Remover o MDM',
    hint: 'Tira o tablet do gerenciamento. Para voltar a gerenciar, só cadastrando de novo pelo cabo USB.',
  },
]

/** Código como texto com os zeros à esquerda (6 dígitos `admin`, 8 `remove` — §12.3). */
function codeText(result) {
  const raw = String(result?.code ?? '').trim()
  const len = result?.purpose === 'remove' ? 8 : 6
  return /^\d+$/.test(raw) ? raw.padStart(len, '0') : raw
}

/** "564667" → "564 667"; "22365767" → "2236 5767". */
function groupCode(code) {
  const s = String(code || '')
  const half = Math.ceil(s.length / 2)
  return s.length > 4 ? `${s.slice(0, half)} ${s.slice(half)}` : s
}

function formatRemaining(ms) {
  const total = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

/**
 * Código de uso único para o técnico digitar no tablet (CONTRACT.md §12.3). Funciona offline (TOTP).
 * `remove` só com `localAdmin.allowRemove` na política efetiva e passa pelo MFA step-up do api.js
 * (mesmo caminho do wipe).
 */
export default function LocalCodeModal({ isOpen, onClose, deviceId, deviceName, allowRemove }) {
  const issue = useIssueMdmLocalCode(deviceId)
  const [purpose, setPurpose] = useState('admin')
  const [reason, setReason] = useState('')
  const [result, setResult] = useState(null) // { code, purpose, validUntil }
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!isOpen) return
    setPurpose('admin')
    setReason('')
    setResult(null)
    setError(null)
    setCopied(false)
  }, [isOpen])

  useEffect(() => {
    if (!result) return undefined
    setNow(Date.now())
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [result])

  const trimmed = reason.trim()
  const reasonOk = trimmed.length >= 3 && trimmed.length <= 300
  const isRemove = purpose === 'remove'

  const handleIssue = async (e) => {
    e.preventDefault()
    if (!reasonOk || (isRemove && !allowRemove)) return
    setError(null)
    try {
      const res = await issue.mutateAsync({ purpose, reason: trimmed })
      setCopied(false)
      setResult(res)
    } catch (err) {
      setError(errorMessage(err, 'Não foi possível gerar o código.'))
    }
  }

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(codeText(result))
      setCopied(true)
    } catch {
      /* sem permissão de área de transferência — o código está na tela */
    }
  }

  const remaining = result?.validUntil ? new Date(result.validUntil).getTime() - now : 0
  const expired = !!result && remaining <= 0
  const resultIsRemove = result?.purpose === 'remove'

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Código de acesso — ${deviceName || 'tablet'}`}>
      {result ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Código para <strong>{resultIsRemove ? 'remover o MDM' : 'o modo técnico'}</strong>:
          </p>
          <div className={clsx('rounded-lg border p-5 text-center',
            expired ? 'bg-gray-50 border-gray-200' : resultIsRemove ? 'bg-red-50 border-red-200' : 'bg-theme-primary/5 border-theme-primary/30')}>
            <div className={clsx('font-mono font-bold tracking-[0.2em] text-4xl sm:text-5xl select-all',
              expired ? 'text-gray-400 line-through' : 'text-gray-900')}>
              {groupCode(codeText(result))}
            </div>
            <div className={clsx('mt-3 text-sm', expired ? 'text-red-600' : 'text-gray-600')}>
              {expired
                ? 'Código expirado — gere outro.'
                : <>Vale por mais <span className="font-mono font-semibold">{formatRemaining(remaining)}</span> (até {formatUntil(result.validUntil)})</>}
            </div>
            {!expired && (
              <button type="button" onClick={copy} className="mt-2 inline-flex items-center gap-1 text-xs text-theme-primary hover:underline">
                <Copy className="w-3.5 h-3.5" /> {copied ? 'Copiado' : 'Copiar'}
              </button>
            )}
          </div>
          <div className="flex items-start gap-2 text-sm text-gray-700">
            <Phone className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-500" />
            <span>
              Leia o código para o técnico por telefone; ele digita no tablet quando o aparelho pedir
              {resultIsRemove ? ' (opção "Remover o MDM")' : ''}. Vale uma vez só e funciona mesmo com o tablet sem internet.
            </span>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setResult(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
              Gerar outro
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-white rounded-lg bg-theme-primary hover:bg-theme-primary/90">
              Fechar
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleIssue} className="space-y-4">
          <p className="text-sm text-gray-600">
            Gere um código para o técnico que está com o tablet na mão. Cada código vale uma vez e por poucos minutos.
          </p>

          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            {PURPOSES.map((p) => {
              const disabled = p.value === 'remove' && !allowRemove
              return (
                <label key={p.value} className={clsx('flex items-start gap-2 px-3 py-2 text-sm', disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-gray-50')}>
                  <input type="radio" className="mt-0.5" checked={purpose === p.value} disabled={disabled} onChange={() => setPurpose(p.value)} />
                  <span>
                    <span className={clsx('text-gray-900', p.value === 'remove' && 'font-medium')}>{p.label}</span>
                    <span className="block text-xs text-gray-500">{p.hint}</span>
                    {disabled && (
                      <span className="block text-xs text-amber-700 mt-0.5">
                        Desligado na política. Para usar, marque &quot;Permitir remover o MDM pelo tablet&quot; em Políticas (do tablet ou do grupo).
                      </span>
                    )}
                  </span>
                </label>
              )
            })}
          </div>

          {isRemove && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <span>
                Com este código o técnico tira o tablet do MDM: a VPN, o quiosque e as restrições saem e o painel perde o
                controle do aparelho. Gerar o código pede o código do autenticador.
              </span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
            <textarea
              rows={2}
              value={reason}
              maxLength={300}
              onChange={(e) => setReason(e.target.value)}
              placeholder={isRemove ? 'ex.: tablet devolvido ao fornecedor' : 'ex.: técnico vai trocar a rede Wi-Fi'}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">Fica registrado no Histórico do tablet com o seu nome.</p>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={issue.isPending || !reasonOk || (isRemove && !allowRemove)}
              className={clsx('flex items-center gap-2 px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50',
                isRemove ? 'bg-red-600 hover:bg-red-700' : 'bg-theme-primary hover:bg-theme-primary/90')}
            >
              {issue.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              {isRemove ? 'Gerar código de remoção' : 'Gerar código'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}
